const DB_NAME = 'ai_chat_session_backups';
const DB_VERSION = 1;
const STORE_NAME = 'daily_snapshots';
const RETENTION_DAYS = 5;

function localDateKey(date = new Date()) {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
}

function retentionCutoff(date = new Date()) {
  const cutoff = new Date(date.getFullYear(), date.getMonth(), date.getDate());
  cutoff.setDate(cutoff.getDate() - (RETENTION_DAYS - 1));
  return localDateKey(cutoff);
}

class SessionBackupDB {
  constructor() {
    this.dbPromise = null;
  }

  open() {
    if (this.dbPromise) return this.dbPromise;
    if (typeof indexedDB === 'undefined') {
      return Promise.reject(new Error('IndexedDB is not available'));
    }

    this.dbPromise = new Promise((resolve, reject) => {
      const request = indexedDB.open(DB_NAME, DB_VERSION);
      request.onupgradeneeded = event => {
        const db = event.target.result;
        if (!db.objectStoreNames.contains(STORE_NAME)) {
          db.createObjectStore(STORE_NAME, { keyPath: 'date' });
        }
      };
      request.onsuccess = () => {
        const db = request.result;
        db.onversionchange = () => db.close();
        resolve(db);
      };
      request.onerror = () => {
        this.dbPromise = null;
        reject(request.error || new Error('Failed to open backup database'));
      };
      request.onblocked = () => {
        this.dbPromise = null;
        reject(new Error('Backup database upgrade is blocked'));
      };
    });
    return this.dbPromise;
  }

  async saveDailySnapshot(sessions, now = new Date()) {
    if (!Array.isArray(sessions) || sessions.length === 0) return null;
    const db = await this.open();
    const date = localDateKey(now);
    const cutoff = retentionCutoff(now);
    const plainSessions = JSON.parse(JSON.stringify(sessions));

    return new Promise((resolve, reject) => {
      const transaction = db.transaction([STORE_NAME], 'readwrite');
      const store = transaction.objectStore(STORE_NAME);
      store.put({ date, sessions: plainSessions, updatedAt: now.getTime() });

      const cursorRequest = store.openCursor();
      cursorRequest.onsuccess = event => {
        const cursor = event.target.result;
        if (!cursor) return;
        if (String(cursor.key) < cutoff) cursor.delete();
        cursor.continue();
      };
      transaction.oncomplete = () => resolve(date);
      transaction.onerror = () => reject(transaction.error || new Error('Failed to save backup'));
      transaction.onabort = () => reject(transaction.error || new Error('Backup save was aborted'));
    });
  }

  async listDailySnapshots() {
    const db = await this.open();
    const cutoff = retentionCutoff();
    return new Promise((resolve, reject) => {
      const transaction = db.transaction([STORE_NAME], 'readwrite');
      const store = transaction.objectStore(STORE_NAME);
      const request = store.openCursor();
      const snapshots = [];
      request.onsuccess = event => {
        const cursor = event.target.result;
        if (!cursor) return;
        const snapshot = cursor.value;
        if (String(snapshot.date) < cutoff) {
          cursor.delete();
          cursor.continue();
          return;
        }
        const sessions = Array.isArray(snapshot.sessions) ? snapshot.sessions : [];
        snapshots.push({
          date: snapshot.date,
          updatedAt: snapshot.updatedAt || 0,
          sessionCount: sessions.length,
          messageCount: sessions.reduce((count, session) => count + (Array.isArray(session.messages) ? session.messages.length : 0), 0)
        });
        cursor.continue();
      };
      request.onerror = () => reject(request.error || new Error('Failed to list backups'));
      transaction.oncomplete = () => {
        snapshots.sort((a, b) => b.date.localeCompare(a.date));
        resolve(snapshots);
      };
      transaction.onerror = () => reject(transaction.error || new Error('Failed to clean backups'));
    });
  }

  async getDailySnapshot(date) {
    const db = await this.open();
    return new Promise((resolve, reject) => {
      const transaction = db.transaction([STORE_NAME], 'readonly');
      const request = transaction.objectStore(STORE_NAME).get(date);
      request.onsuccess = () => resolve(request.result || null);
      request.onerror = () => reject(request.error || new Error('Failed to read backup'));
    });
  }

  async clearAll() {
    const db = await this.open();
    return new Promise((resolve, reject) => {
      const transaction = db.transaction([STORE_NAME], 'readwrite');
      transaction.objectStore(STORE_NAME).clear();
      transaction.oncomplete = resolve;
      transaction.onerror = () => reject(transaction.error || new Error('Failed to clear backups'));
      transaction.onabort = () => reject(transaction.error || new Error('Backup clear was aborted'));
    });
  }
}

export const sessionBackupDB = new SessionBackupDB();
